import { useState } from 'react';
import {
  FormControlLabel,
  Switch,
  useTheme,
  Button,
  Typography,
  Stack,
  Paper,
  Popper,
  Radio,
  RadioGroup,
  ClickAwayListener,
} from '@mui/material';
import { ArrowDropDownIcon } from '@mui/x-date-pickers-pro';
import useIsMobile from 'hooks/useIsMobile';
import { useRecoilState, useRecoilValue } from 'recoil';

import { SGTooltip } from 'components/core';
import {
  newTVHiroState,
  hiroV3AvailableState,
  hiroVersionState,
  hiroVersionSetTimestampState,
} from 'states';

import { dayjs, ET } from 'util/shared/date';

const HiroVersions = [
  {
    version: 1,
    title: 'HIRO - Legacy',
  },
  {
    version: 2,
    title: 'HIRO - October 2025 Version',
  },
  { version: 3, title: 'HIRO - New' },
];
const HiroVersionFromVersion = new Map(HiroVersions.map((v) => [v.version, v]));

export const HiroHeaderWidgetPostamble = () => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [newTVHiro, setNewTVHiro] = useRecoilState(newTVHiroState);
  const hiroV3Available = useRecoilValue(hiroV3AvailableState);
  const [hiroVersionStateVal, setHiroVersion] =
    useRecoilState(hiroVersionState);
  const [hiroVersionSetTimestamp, setHiroVersionSetTimestamp] = useRecoilState(
    hiroVersionSetTimestampState,
  );
  let hiroVersion = 3;
  if (
    hiroVersionStateVal != null &&
    hiroVersionSetTimestamp != null &&
    hiroVersionSetTimestamp > dayjs().tz(ET).startOf('day').valueOf()
  ) {
    hiroVersion = hiroVersionStateVal;
  }

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return null;

  return hiroV3Available ? (
    <>
      <Button
        onClick={handleClick}
        variant="outlined"
        color="primary"
        endIcon={<ArrowDropDownIcon />}
        sx={{
          borderRadius: '4px',
          minWidth: '100px',
          fontSize: isMobile ? 11 : 13,
          fontWeight: 500,
          textTransform: 'none',
          px: 1.5,
          py: 0.5,
        }}
      >
        {HiroVersionFromVersion.get(hiroVersion)?.title ?? hiroVersion}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{ zIndex: 2 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            sx={{
              mt: 1,
              p: 4,
              boxShadow: theme.palette.shadows.default,
              borderRadius: '4px',
              width: 260,
            }}
          >
            <RadioGroup
              value={hiroVersion}
              defaultValue={hiroVersion}
              onChange={(_e, value) => {
                setHiroVersion(parseInt(value));
                setHiroVersionSetTimestamp(+new Date());
              }}
              sx={{ gap: '12px' }}
            >
              {HiroVersions.map(({ version, title }) => (
                <FormControlLabel
                  value={version}
                  control={<Radio />}
                  label={
                    <Stack>
                      <Typography
                        sx={{
                          fontSize: { xs: 12, md: 14 },
                          color:
                            hiroVersion == version
                              ? 'primary.main'
                              : 'text.secondary',
                        }}
                      >
                        {title}
                      </Typography>
                    </Stack>
                  }
                />
              ))}
            </RadioGroup>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  ) : (
    <SGTooltip title="Use the new version of HIRO which works on top of the Trading View charting library">
      <FormControlLabel
        sx={{
          marginLeft: '1.5ex',
          '& .MuiFormControlLabel-label': {
            color: theme.palette.primary.main,
            textAlign: 'center',
            fontSize: { xs: 12, md: 14 },
          },
        }}
        control={
          <Switch
            size={isMobile ? 'small' : 'medium'}
            sx={{
              '& .MuiSwitch-track': {
                opacity: 0.25,
                backgroundColor: theme.palette.success.main,
              },
              '&.Mui-checked .MuiSwitch-track': {
                opacity: 1,
              },
            }}
            color="success"
            checked={newTVHiro}
            onChange={() => setNewTVHiro(!newTVHiro)}
          />
        }
        label="HIRO Upgrade"
        labelPlacement={isMobile ? 'top' : 'start'}
      />
    </SGTooltip>
  );
};
